import {
  LOOK_CATALOGUE_VERSION,
  type CatalogueAnimation,
  type CatalogueCell,
  type CatalogueName,
  type CatalogueProjectile,
  type CatalogueSlot,
  type LookCatalogue,
} from "./look-catalogue.ts";
import { DIRECTION_NAMES, type DirectionName } from "./sheet.ts";

/**
 * Validating a parsed look catalogue (P1.9-T93, `look-catalogue.ts`): the shape of every part, and that nothing in it
 * is listed twice or names something the catalogue does not hold, so what reads a catalogue (a viewer, a character
 * creator) never looks up an id that is not there and never divides by a projectile's zero frames.
 */

/**
 * Narrows to a plain object.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @returns The object.
 * @throws Error when it is not one.
 */
function asObject(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`look catalogue ${where} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Narrows to an array.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @returns The array.
 * @throws Error when it is not one.
 */
function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`look catalogue ${where} must be a list`);
  }
  return value;
}

/**
 * Reads a string field.
 *
 * @param object The holder.
 * @param key The field.
 * @param where The holder's name, for the error.
 * @returns The string.
 * @throws Error when it is not a string.
 */
function text(object: Record<string, unknown>, key: string, where: string): string {
  const value = object[key];
  if (typeof value !== "string") {
    throw new Error(`look catalogue ${where}.${key} must be a string`);
  }
  return value;
}

/**
 * Reads a string or null field.
 *
 * @param object The holder.
 * @param key The field.
 * @param where The holder's name, for the error.
 * @returns The string, or null.
 * @throws Error when it is neither.
 */
function textOrNull(object: Record<string, unknown>, key: string, where: string): string | null {
  return object[key] === null ? null : text(object, key, where);
}

/**
 * Reads a finite number field.
 *
 * @param object The holder.
 * @param key The field.
 * @param where The holder's name, for the error.
 * @returns The number.
 * @throws Error when it is not a finite number.
 */
function numeric(object: Record<string, unknown>, key: string, where: string): number {
  const value = object[key];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`look catalogue ${where}.${key} must be a number`);
  }
  return value;
}

/**
 * Reads a list of non-negative whole numbers.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @returns The numbers.
 * @throws Error when it is not such a list.
 */
function wholeNumbers(value: unknown, where: string): number[] {
  const list = asArray(value, where);
  if (!list.every((entry) => typeof entry === "number" && Number.isInteger(entry) && entry >= 0)) {
    throw new Error(`look catalogue ${where} must be a list of whole numbers`);
  }
  return list as number[];
}

/**
 * Reads a list of ids and labels.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @returns The names.
 * @throws Error when an entry is malformed.
 */
function names(value: unknown, where: string): CatalogueName[] {
  return asArray(value, where).map((entry, index) => {
    const name = asObject(entry, `${where}[${index}]`);
    return { id: text(name, "id", `${where}[${index}]`), label: text(name, "label", `${where}[${index}]`) };
  });
}

/**
 * Validates one animation.
 *
 * @param value The parsed animation.
 * @param where Its name, for the error.
 * @returns The animation.
 * @throws Error when a field is malformed, it has no frame or no direction, or a direction's cells are not one per
 *   frame.
 */
function validateAnimation(value: unknown, where: string): CatalogueAnimation {
  const animation = asObject(value, where);
  const durations = asArray(animation["durations"], `${where}.durations`);
  if (durations.length === 0 || !durations.every((entry) => typeof entry === "number" && Number.isFinite(entry) && entry > 0)) {
    throw new Error(`look catalogue ${where}.durations must be a list of times in ms, one per frame`);
  }
  const given = asObject(animation["cells"], `${where}.cells`);
  const cells: Partial<Record<DirectionName, CatalogueCell[]>> = {};
  for (const direction of DIRECTION_NAMES) {
    if (given[direction] === undefined) {
      continue;
    }
    const list = asArray(given[direction], `${where}.cells.${direction}`);
    if (list.length !== durations.length) {
      throw new Error(`look catalogue ${where}.cells.${direction} must hold one cell per frame`);
    }
    cells[direction] = list.map((cell, index) => {
      const pair = wholeNumbers(cell, `${where}.cells.${direction}[${index}]`);
      if (pair.length !== 2) {
        throw new Error(`look catalogue ${where}.cells.${direction}[${index}] must be a column and a row`);
      }
      return [pair[0] as number, pair[1] as number];
    });
  }
  if (Object.keys(cells).length === 0) {
    throw new Error(`look catalogue ${where}.cells has no direction`);
  }
  if (typeof animation["loop"] !== "boolean") {
    throw new Error(`look catalogue ${where}.loop must be true or false`);
  }
  const slotPages: Record<string, string> = {};
  for (const slot of Object.keys(asObject(animation["slotPages"], `${where}.slotPages`))) {
    slotPages[slot] = text(animation["slotPages"] as Record<string, unknown>, slot, `${where}.slotPages`);
  }
  return { id: text(animation, "id", where), label: text(animation, "label", where), group: text(animation, "group", where), set: textOrNull(animation, "set", where), page: text(animation, "page", where), loop: animation["loop"], durations: durations as number[], cells, gameStrip: textOrNull(animation, "gameStrip", where), slotPages };
}

/**
 * Validates one slot.
 *
 * @param value The parsed slot.
 * @param where Its name, for the error.
 * @param known The ids of the catalogue's animations.
 * @returns The slot.
 * @throws Error when a field is malformed or an item names an animation the catalogue lacks.
 */
function validateSlot(value: unknown, where: string, known: ReadonlySet<string>): CatalogueSlot {
  const slot = asObject(value, where);
  if (typeof slot["tool"] !== "boolean") {
    throw new Error(`look catalogue ${where}.tool must be true or false`);
  }
  const items = asArray(slot["items"], `${where}.items`).map((entry, index) => {
    const at = `${where}.items[${index}]`;
    const item = asObject(entry, at);
    const animations = asArray(item["animations"], `${at}.animations`);
    for (const id of animations) {
      if (typeof id !== "string" || !known.has(id)) {
        throw new Error(`look catalogue ${at} names the animation "${String(id)}", which the catalogue lacks`);
      }
    }
    return { code: text(item, "code", at), label: text(item, "label", at), variants: wholeNumbers(item["variants"], `${at}.variants`), animations: animations as string[], set: textOrNull(item, "set", at) };
  });
  let needs: CatalogueSlot["needs"] = null;
  if (slot["needs"] !== null) {
    const given = asObject(slot["needs"], `${where}.needs`);
    const codes = asArray(given["codes"], `${where}.needs.codes`);
    if (!codes.every((code) => typeof code === "string")) {
      throw new Error(`look catalogue ${where}.needs.codes must be a list of item codes`);
    }
    needs = { slot: text(given, "slot", `${where}.needs`), codes: codes as string[] };
  }
  return { id: text(slot, "id", where), label: text(slot, "label", where), param: textOrNull(slot, "param", where), tool: slot["tool"], follows: textOrNull(slot, "follows", where), needs, rank: numeric(slot, "rank", where), items };
}

/**
 * Validates the tool slots' rank tables.
 *
 * @param value The parsed tables.
 * @returns The tables.
 * @throws Error when a table is not rows of numbers.
 */
function validateOrder(value: unknown): Record<string, Record<string, number[][]>> {
  const order: Record<string, Record<string, number[][]>> = {};
  for (const [slot, pages] of Object.entries(asObject(value, "order"))) {
    const byPage: Record<string, number[][]> = {};
    for (const [page, rows] of Object.entries(asObject(pages, `order.${slot}`))) {
      byPage[page] = asArray(rows, `order.${slot}.${page}`).map((row, index) => {
        const ranks = asArray(row, `order.${slot}.${page}[${index}]`);
        if (!ranks.every((rank) => typeof rank === "number" && Number.isFinite(rank))) {
          throw new Error(`look catalogue order.${slot}.${page}[${index}] must be a list of numbers`);
        }
        return ranks as number[];
      });
    }
    order[slot] = byPage;
  }
  return order;
}

/**
 * Reads a whole number above 0.
 *
 * @param object The holder.
 * @param key The field.
 * @param where The holder's name, for the error.
 * @returns The number.
 * @throws Error when it is anything else.
 */
function positiveWhole(object: Record<string, unknown>, key: string, where: string): number {
  const value = object[key];
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    throw new Error(`look catalogue ${where}.${key} must be a whole number above 0`);
  }
  return value;
}

/**
 * Refuses a list that names one id twice.
 *
 * @param ids The ids.
 * @param what What each one is, for the error: "animation", "slot" and so on.
 * @throws Error naming the first id listed twice.
 */
function unique(ids: readonly string[], what: string): void {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) {
      throw new Error(`look catalogue ${what} "${id}" is listed twice`);
    }
    seen.add(id);
  }
}

/**
 * Validates the projectiles.
 *
 * @param value The parsed list.
 * @returns The projectiles.
 * @throws Error when one is malformed, or has no frame, no time or no size.
 */
function validateProjectiles(value: unknown): CatalogueProjectile[] {
  return asArray(value, "projectiles").map((entry, index) => {
    const at = `projectiles[${index}]`;
    const projectile = asObject(entry, at);
    return {
      id: text(projectile, "id", at),
      label: text(projectile, "label", at),
      path: text(projectile, "path", at),
      frameWidth: positiveWhole(projectile, "frameWidth", at),
      frameHeight: positiveWhole(projectile, "frameHeight", at),
      frames: positiveWhole(projectile, "frames", at),
      frameMs: positiveWhole(projectile, "frameMs", at),
      slot: textOrNull(projectile, "slot", at),
      item: textOrNull(projectile, "item", at),
    };
  });
}

/**
 * Validates a parsed catalogue.
 *
 * @param value Parsed JSON.
 * @returns The catalogue.
 * @throws Error naming what is malformed: another version, a missing part, an animation without frames or directions,
 *   an item naming an animation the catalogue lacks, an id listed twice, or anything `crossCheck` refuses.
 */
export function validateLookCatalogue(value: unknown): LookCatalogue {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("look catalogue must be an object");
  }
  const catalogue = value as Record<string, unknown>;
  if (catalogue["version"] !== LOOK_CATALOGUE_VERSION) {
    throw new Error(`look catalogue is version ${String(catalogue["version"])}; this page reads version ${LOOK_CATALOGUE_VERSION}`);
  }
  const pivot = asObject(catalogue["pivot"], "pivot");
  const animations = asArray(catalogue["animations"], "animations").map((entry, index) => validateAnimation(entry, `animations[${index}]`));
  const known = new Set(animations.map((animation) => animation.id));
  const slots = asArray(catalogue["slots"], "slots").map((entry, index) => validateSlot(entry, `slots[${index}]`, known));
  const checked: LookCatalogue = {
    version: LOOK_CATALOGUE_VERSION,
    cell: numeric(catalogue, "cell", "root"),
    pivot: { x: numeric(pivot, "x", "pivot"), y: numeric(pivot, "y", "pivot") },
    pagesPath: text(catalogue, "pagesPath", "root"),
    groups: names(catalogue["groups"], "groups"),
    sets: names(catalogue["sets"], "sets"),
    animations,
    slots,
    order: validateOrder(catalogue["order"]),
    projectiles: validateProjectiles(catalogue["projectiles"]),
  };
  crossCheck(checked);
  return checked;
}

/**
 * Refuses a catalogue whose parts do not agree: an id listed twice, or a group, a set, a slot, an item or a page named
 * by one part and held by none.
 *
 * @param catalogue The catalogue, each part already of the right shape.
 * @throws Error naming the first disagreement.
 */
function crossCheck(catalogue: LookCatalogue): void {
  unique(catalogue.groups.map((group) => group.id), "group");
  unique(catalogue.sets.map((set) => set.id), "set");
  unique(catalogue.animations.map((animation) => animation.id), "animation");
  unique(catalogue.slots.map((slot) => slot.id), "slot");
  unique(catalogue.projectiles.map((projectile) => projectile.id), "projectile");
  const groups = new Set(catalogue.groups.map((group) => group.id));
  const sets = new Set(catalogue.sets.map((set) => set.id));
  const pages = new Set<string>();
  for (const animation of catalogue.animations) {
    if (!groups.has(animation.group)) {
      throw new Error(`look catalogue animation ${animation.id} is in the group "${animation.group}", which is not listed`);
    }
    if (animation.set !== null && !sets.has(animation.set)) {
      throw new Error(`look catalogue animation ${animation.id} is in the set "${animation.set}", which is not listed`);
    }
    pages.add(animation.page);
    for (const page of Object.values(animation.slotPages)) {
      pages.add(page);
    }
  }
  const slots = new Map(catalogue.slots.map((slot) => [slot.id, slot]));
  for (const slot of catalogue.slots) {
    const codes = slot.items.map((item) => item.code);
    const twice = codes.find((code, index) => codes.indexOf(code) !== index);
    if (twice !== undefined) {
      throw new Error(`look catalogue slot ${slot.id} lists the item "${twice}" twice`);
    }
    for (const item of slot.items) {
      if (item.set !== null && !sets.has(item.set)) {
        throw new Error(`look catalogue item ${item.code} of slot ${slot.id} is in the set "${item.set}", which is not listed`);
      }
    }
    if (slot.follows !== null && !slots.has(slot.follows)) {
      throw new Error(`look catalogue slot ${slot.id} follows "${slot.follows}", which is no slot`);
    }
    if (slot.needs === null) {
      continue;
    }
    const needed = slots.get(slot.needs.slot);
    if (needed === undefined) {
      throw new Error(`look catalogue slot ${slot.id} needs "${slot.needs.slot}", which is no slot`);
    }
    for (const code of slot.needs.codes) {
      if (!needed.items.some((item) => item.code === code)) {
        throw new Error(`look catalogue slot ${slot.id} needs the item "${code}" of ${needed.id}, which that slot lacks`);
      }
    }
  }
  for (const projectile of catalogue.projectiles) {
    if (projectile.slot === null) {
      if (projectile.item !== null) {
        throw new Error(`look catalogue projectile ${projectile.id} names an item and no slot`);
      }
      continue;
    }
    const slot = slots.get(projectile.slot);
    if (slot === undefined) {
      throw new Error(`look catalogue projectile ${projectile.id} belongs to the slot "${projectile.slot}", which is no slot`);
    }
    if (!slot.items.some((item) => item.code === projectile.item)) {
      throw new Error(`look catalogue projectile ${projectile.id} belongs to the item "${String(projectile.item)}", which slot ${slot.id} lacks`);
    }
  }
  for (const [slot, tables] of Object.entries(catalogue.order)) {
    if (!slots.has(slot)) {
      throw new Error(`look catalogue order names the slot "${slot}", which is no slot`);
    }
    for (const page of Object.keys(tables)) {
      if (!pages.has(page)) {
        throw new Error(`look catalogue order.${slot} names the page "${page}", which no animation is on`);
      }
    }
  }
}
